import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  MessageCircle,
  Send,
  Loader2,
  Bot,
  User,
  Trash2,
  HelpCircle,
  AlertTriangle
} from 'lucide-react';

const AIChat = ({ context = null, currentQuestion = null, isExamMode = false, isOpen, onOpenChange }) => {
  const { getAuthHeaders, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API}/chat/history?limit=50`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const formattedMessages = [];
        data.history.forEach(item => {
          formattedMessages.push({
            role: 'user',
            content: item.user_message,
            timestamp: item.created_at
          });
          formattedMessages.push({
            role: 'assistant',
            content: item.ai_response,
            timestamp: item.created_at
          });
        });
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      // Build context from current question if available
      let questionContext = context;
      if (currentQuestion) {
        questionContext = `
Current Exam Question:
Question: ${currentQuestion.questionText || currentQuestion.question_text}
Type: ${currentQuestion.questionType || currentQuestion.question_type}
${currentQuestion.choices ? `Answer Options: ${currentQuestion.choices.map((c, i) => `${String.fromCharCode(65 + i)}) ${c.text}`).join(', ')}` : ''}
${currentQuestion.correctAnswers ? `Correct Answer(s): ${currentQuestion.correctAnswers.join(', ')}` : ''}
`;
      }

      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          context: questionContext
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      toast.error('Error getting AI response');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearHistory = async () => {
    try {
      await fetch(`${API}/chat/history`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      setMessages([]);
      toast.success('Chat history cleared');
    } catch (error) {
      toast.error('Error clearing history');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Context-aware suggested questions - generalist approach for all exam types
  const getSuggestedQuestions = () => {
    const track = user?.selected_track || 'PMP';
    
    // If there's a current question, show question-specific suggestions
    if (currentQuestion) {
      return [
        "Why is this answer correct?",
        "Why are the other answers wrong?",
        "Can you simplify the explanation for this question?",
        "What concept is being tested in this question?"
      ];
    }
    
    // Generalist suggestions that work for all exam types
    const generalQuestions = [
      "What are the key exam tips for my certification?",
      "Explain the main concepts I need to know",
      "What are common mistakes candidates make?",
      "Help me understand a specific topic",
      "Compare predictive vs. agile approaches",
      "Can you help me with a practice question?"
    ];
    
    // Add one track-specific question at the beginning
    const trackSpecificQuestion = {
      'PMP': "What are the 5 process groups in PMBOK?",
      'CAPM': "What fundamentals should I focus on for CAPM?",
      'PSM': "What are the Scrum Master responsibilities?",
      'PSPO': "How do I manage a Product Backlog effectively?",
      'ITIL4': "What are the ITIL guiding principles?",
      'PRINCE2': "Explain the PRINCE2 principles",
      'SAFe': "What is PI Planning in SAFe?"
    };
    
    const specific = trackSpecificQuestion[track] || trackSpecificQuestion['PMP'];
    return [specific, ...generalQuestions.slice(0, 5)];
  };

  // Get track title - generalist
  const getTrackTitle = () => {
    const track = user?.selected_track || 'PMP';
    return `Certification Expert - ${track}`;
  };

  // Exam mode - AI is disabled
  if (isExamMode) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:w-[450px] p-0 flex flex-col bg-slate-50 dark:bg-slate-900">
          <SheetHeader className="p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-slate-400 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                Lernassistent
              </SheetTitle>
            </div>
          </SheetHeader>

          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-950/30 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Exam Mode Active</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              The AI assistant is disabled during exam simulation 
              to ensure realistic exam conditions.
            </p>
            <p className="text-slate-500 dark:text-slate-500 text-xs mt-4">
              The assistant will be available again after completing the exam.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[450px] p-0 flex flex-col">
        <SheetHeader className="p-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-orange-500 to-red-600">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-white">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              Learning Assistant
            </SheetTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={clearHistory}
                className="h-8 w-8 text-white hover:bg-white/20"
                data-testid="clear-chat-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-white/80">
            {getTrackTitle()} - {user?.selected_track || 'PMP'}
          </p>
        </SheetHeader>

        {/* Info Banner - Generalist Expert */}
        <div className="px-4 py-3 bg-blue-50 dark:bg-blue-950/20 border-b border-blue-200 dark:border-blue-900">
          <div className="flex items-start gap-2">
            <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-800 dark:text-blue-200">
              <strong>Certification Expert:</strong> I can help with PMP, CAPM, Scrum (PSM/PSPO), ITIL, PRINCE2, and SAFe certifications. Ask me anything about exam prep, concepts, or practice questions!
            </p>
          </div>
        </div>

        <ScrollArea ref={scrollRef} className="flex-1 p-4">
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-950/30 mx-auto mb-4 flex items-center justify-center">
                <Bot className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold mb-2">
                {currentQuestion ? "Question about the current exam question?" : "How can I help you?"}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                {currentQuestion 
                  ? "I'm happy to explain why certain answers are correct or incorrect."
                  : "Ask me anything about PMP, CAPM, Scrum Master, Product Owner, ITIL, PRINCE2, or SAFe certifications!"}
              </p>
              <div className="space-y-2">
                {getSuggestedQuestions().map((q, idx) => (
                  <Button
                    key={`suggestion-${q.slice(0, 20)}-${idx}`}
                    variant="outline"
                    size="sm"
                    className="w-full text-left justify-start h-auto py-2 px-3 text-sm"
                    onClick={() => {
                      setInput(q);
                      inputRef.current?.focus();
                    }}
                    data-testid={`suggested-question-${idx}`}
                  >
                    {q}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || `msg-${msg.role}-${idx}`}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    msg.role === 'user' 
                      ? 'bg-orange-100 dark:bg-orange-950/30' 
                      : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 text-orange-600" />
                    ) : (
                      <Bot className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                    )}
                  </div>
                  <div className={`flex-1 max-w-[85%] ${
                    msg.role === 'user' ? 'text-right' : ''
                  }`}>
                    <div className={`inline-block p-3 rounded-xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-orange-500 text-white rounded-br-none'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-none'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800 rounded-bl-none">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about the exam question..."
              className="flex-1"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="send-message-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Floating Chat Button Component
export const ChatButton = ({ context = null, currentQuestion = null, isExamMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 md:bottom-6 right-6 w-14 h-14 rounded-full shadow-lg z-40 ${
          isExamMode 
            ? 'bg-slate-400 hover:bg-slate-500 cursor-not-allowed' 
            : 'bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
        } text-white`}
        data-testid="chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </Button>
      <AIChat 
        context={context} 
        currentQuestion={currentQuestion}
        isExamMode={isExamMode}
        isOpen={isOpen} 
        onOpenChange={setIsOpen} 
      />
    </>
  );
};

export default AIChat;
